import { FactoryProvider, inject } from '@angular/core';

import { TranslocoService } from '@jsverse/transloco';

import { AuthenticationFacadeService } from '@finverity/authentication';
import { CompanyType } from '@finverity/graphql';
import { IDENTITY_ACCESS_TOKEN } from '@finverity/identity-access';
import { FV_SIDEBAR_MENU_ITEMS, groupMenuItemsForCollapsedView, type FvSidebarMenuItemsData } from '@fv/ui-kit';

import { NavigationMenuPermissions } from '../../../identity-access';
import { NAVIGATION_SIDEBAR_MENU_ITEMS } from '../constants';
import { NavigationSidebarMenuItem } from '../types';
import { isMenuItemHiddenInEnvironment } from '../utils';

function isMenuItemCompatibleWithAuthUserCompanyType(
  menuItem: NavigationSidebarMenuItem,
  authCompanyType: CompanyType
): boolean {
  return !menuItem.companyTypes || menuItem.companyTypes.includes(authCompanyType);
}

/**
 * Recursively filters navigation menu items based on user permissions and company type compatibility.
 *
 * This function performs a deep filtering operation that:
 * 1. Excludes menu items incompatible with the user's company type
 * 2. Excludes menu items for which the user lacks required permissions
 * 3. Recursively processes child menu items with the same filtering logic
 * 4. Only includes parent items if they have at least one visible child
 *
 * @param menuItems - Array of navigation menu items to filter
 * @param permissionsMap - Map of navigation permissions where keys are permission names and values indicate if the user has that permission
 * @param companyType - The authenticated user's company type used for compatibility checking
 *
 * @returns Filtered array of navigation menu items that the user can access, with children arrays also filtered recursively
 */
function filterMenuItemsByCompanyTypeAndPermissions(
  menuItems: NavigationSidebarMenuItem[],
  permissionsMap: Record<NavigationMenuPermissions, boolean>,
  companyType: CompanyType
): NavigationSidebarMenuItem[] {
  const filteredItems: NavigationSidebarMenuItem[] = [];

  for (const item of menuItems) {
    if (isMenuItemHiddenInEnvironment(item.identifier)) {
      continue;
    }

    if (!isMenuItemCompatibleWithAuthUserCompanyType(item, companyType)) {
      continue;
    }

    if (item.permission && !permissionsMap[item.permission as NavigationMenuPermissions]) {
      continue;
    }

    let filteredItem = { ...item };

    if (item.children) {
      const filteredChildren = filterMenuItemsByCompanyTypeAndPermissions(item.children, permissionsMap, companyType);

      if (filteredChildren.length > 0) {
        filteredItem = { ...filteredItem, children: filteredChildren };
        filteredItems.push(filteredItem);
      }
    } else {
      filteredItems.push(filteredItem);
    }
  }

  return filteredItems;
}

/**
 * Recursively translates the `title` (and `titleForHeader`) of each menu item.
 * `<fv-sidebar>` renders `item.title` raw, so the i18n keys held in the menu
 * constant must be resolved here before feeding `FV_SIDEBAR_MENU_ITEMS`.
 */
function translateMenuItems(
  menuItems: NavigationSidebarMenuItem[],
  translocoService: TranslocoService
): NavigationSidebarMenuItem[] {
  return menuItems.map(item => ({
    ...item,
    title: translocoService.translate(item.title),
    ...(item.titleForHeader ? { titleForHeader: translocoService.translate(item.titleForHeader) } : {}),
    ...(item.children ? { children: translateMenuItems(item.children, translocoService) } : {}),
  }));
}

function navigationSidebarMenuItemsFactory(): FvSidebarMenuItemsData {
  const authUser = inject(AuthenticationFacadeService).userSignal();
  const permissionsMap = inject<Record<NavigationMenuPermissions, boolean>>(IDENTITY_ACCESS_TOKEN);
  const translocoService = inject(TranslocoService);

  const filteredMenuItems = filterMenuItemsByCompanyTypeAndPermissions(
    NAVIGATION_SIDEBAR_MENU_ITEMS,
    permissionsMap,
    authUser?.company_type as CompanyType
  );

  const menuItems = translateMenuItems(filteredMenuItems, translocoService);

  return {
    menuItemsInExpandedSidebar: menuItems,
    menuItemsInCollapsedSidebar: groupMenuItemsForCollapsedView(menuItems),
  };
}

export function provideNavigationMenuItems(): FactoryProvider {
  return {
    provide: FV_SIDEBAR_MENU_ITEMS,
    useFactory: navigationSidebarMenuItemsFactory,
  };
}
